/* eslint-disable consistent-return */
import { hydra } from '@/lib/services/Hydra'
import type { HydraErrorType } from '@/lib/services/Hydra/_hydraError'
import { removeAuthCookie } from '@/lib/utils/shared/cookies/removeAuthCookie'
import { setAuthCookie } from '@/lib/utils/shared/cookies/setAuthCookie'
import useGlobalStore from '@/store/useGlobalStore'
import { useState } from 'react'
import { getAlpha2WithExceptions } from '@/components/Checkout/_helpers/functions/getAlpha2WithExceptions'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { mixpanelSigninEventLocation } from '@/lib/services/mixpanel/helpers/eventLocation'
import { generateCartEventData } from '@/lib/services/mixpanel/helpers/generateCartEventData'
import { datadogRum } from '@datadog/browser-rum'
import { setCookie, deleteCookie } from 'cookies-next/client'
import { logError } from '@/lib/utils/shared/log'
import { useGTM } from '../google-analytics/useGTM'

export type SignInPossiblePagesType = 'checkout' | 'sign_in' | 'account_pop_up'

type Props = {
  isSignInAfterAccountCreation?: boolean
}

export const useSignInUser = ({
  isSignInAfterAccountCreation = false,
}: Props) => {
  const { setUser, app, cart } = useGlobalStore()
  const { gtm } = useGTM()

  const [isLoadingSignIn, setIsLoadingSignIn] = useState(false)
  const [signInError, setSignInError] = useState<HydraErrorType | null>(null)

  const signInWithToken = async ({
    token,
    keepSignedIn = false,
    registered = true,
    page,
  }: {
    token: string
    keepSignedIn: boolean
    registered: boolean
    page?: SignInPossiblePagesType
    // eslint-disable-next-line sonarjs/cognitive-complexity
  }) => {
    const customer = await hydra.getMe({ hydraToken: token })

    const setMixPanelSignInProperties = async (): Promise<void> => {
      if (!customer) {
        return
      }
      const unicityId =
        typeof customer.id.unicity === 'object'
          ? (customer.id.unicity as { unicity: string }).unicity
          : customer.id.unicity

      mixpanelService.identify(unicityId)

      // Set Datadog user identification
      datadogRum.setUser({
        id: unicityId,
        type: customer.type,
        market: getAlpha2WithExceptions({ alpha2: app.alpha2 }),
      })

      const enrollerId = customer.enroller.id.unicity.toString()
      const sponsorId = customer.sponsor.id.unicity.toString()

      // Set super properties for all users
      mixpanelService.registerSuperProperties({
        enroller_id: enrollerId,
        sponsor_id: sponsorId || enrollerId,
        user_market: getAlpha2WithExceptions({ alpha2: app.alpha2 }),
        user_market_extended: app.alpha2,
        user_language: app.language,
        is_user_logged_in: 'true',
        user_type: customer.type,
      })

      try {
        // Set people properties for all users
        await mixpanelService.setPeopleProperties({
          unicity_id: unicityId,
          enroller_id: enrollerId,
          sponsor_id: sponsorId || enrollerId,
          user_market: getAlpha2WithExceptions({ alpha2: app.alpha2 }),
          user_market_extended: app.alpha2,
          user_language: app.language,
          membership_registration_date: customer.joinDate,
          user_type: customer.type,
        })

        setCookie('userInitializedInMixpanel', true)
      } catch (error) {
        logError('Error setting Mixpanel properties:', error)
        await mixpanelService.reset()

        deleteCookie('userInitializedInMixpanel')
      }
    }

    if (!customer || customer.error) {
      setUser({ authError: true, isSignedIn: false })
      removeAuthCookie({}) // Error signing in, remove cookie for middleware validation safety

      setIsLoadingSignIn(false)
      setSignInError(customer)
      return
    }

    if (!isSignInAfterAccountCreation) {
      gtm.signInCompleted({
        user_id: customer.id.unicity,
        user_market: getAlpha2WithExceptions({ alpha2: app.alpha2 }),
        user_market_extended: app.alpha2,
        user_language: app.language,
        enroller_id: customer.enroller.id.unicity,
        sponsor_id: customer.sponsor.id.unicity,
        referral_code: undefined, // not available on login
        membership_registration_date: customer.joinDate,
        user_type: customer.type,
        is_user_logged_in: true,
      })

      await setMixPanelSignInProperties()

      try {
        await mixpanelService.trackEvent(MixpanelEvent.SIGN_IN_COMPLETED, {
          event_location: mixpanelSigninEventLocation(page),
        })
      } catch (error) {
        logError('Error tracking sign in event:', error)
      }
    }

    if (page === 'checkout') {
      gtm.checkoutAccountDataAdded({ scenario: 'login' })

      try {
        await mixpanelService.trackEvent(
          MixpanelEvent.CHECKOUT_ACCOUNT_DATA_ADDED,
          {
            cart: generateCartEventData(
              cart,
              app.marketExceptions?.currencyCode || 'USD',
              customer.type,
            ),
            scenario: 'login',
          },
        )
      } catch (error) {
        logError('Error tracking checkout account data event:', error)
      }
    }

    setAuthCookie({ token })

    setUser({
      isSignedIn: true,
      token,
      customerType: customer.type,
      keepSignedIn,
      registered,
      customer,
    })

    setIsLoadingSignIn(false)

    return customer
  }

  const signInUser = async ({
    email,
    keepSignedIn = false,
    password,
    registered = true,
    page,
  }: {
    email: string
    keepSignedIn: boolean
    password: string
    registered: boolean
    page?: SignInPossiblePagesType
  }) => {
    if (isLoadingSignIn) return null

    try {
      setIsLoadingSignIn(true)
      setUser({ authError: false })

      const loginTokenData = await hydra.getLoginToken({
        username: email,
        password,
      })

      if (!loginTokenData || loginTokenData.error) {
        setUser({ authError: true })
        setIsLoadingSignIn(false)
        setSignInError(loginTokenData)
        return loginTokenData
      }

      const customer = await signInWithToken({
        token: loginTokenData.token,
        keepSignedIn,
        registered,
        page,
      })

      return customer
    } catch {
      setIsLoadingSignIn(false)
      setUser({ authError: true, isSignedIn: false })
      removeAuthCookie({}) // in case of an unexpected error remove cookie for middleware validation safety
      return null
    }
  }

  return { signInUser, isLoadingSignIn, signInError, signInWithToken }
}
